// src/components/ImageCropper.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

const BRAND = "#00675f";

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

function getPointer(e) {
  const p = e.touches && e.touches[0] ? e.touches[0] : e;
  return { x: p.clientX, y: p.clientY };
}

export default function ImageCropper({ file, aspect = 16 / 9, outputWidth = 1440, backgroundColor = "#f7efe6", fitMode = "contain", onCancel, onComplete }) {
  const imgRef = useRef(null);
  const stageRef = useRef(null);
  const dragRef = useRef(null);
  const [imageUrl, setImageUrl] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [zoom, setZoom] = useState(1.18);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    setLoaded(false);
    setZoom(1);
    setPos({ x: 0, y: 0 });
    setNaturalSize({ width: 0, height: 0 });
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const cropBox = useMemo(() => {
    const width = 560;
    const height = Math.round(width / (aspect || 1));
    if (height > 420) {
      const h = 420;
      return { width: Math.round(h * (aspect || 1)), height: h };
    }
    return { width, height };
  }, [aspect]);

  const handleStart = (e) => {
    e.preventDefault();
    const p = getPointer(e);
    dragRef.current = { startX: p.x, startY: p.y, baseX: pos.x, baseY: pos.y };
  };

  const handleMove = (e) => {
    if (!dragRef.current) return;
    e.preventDefault();
    const p = getPointer(e);
    const dx = p.x - dragRef.current.startX;
    const dy = p.y - dragRef.current.startY;
    setPos({ x: dragRef.current.baseX + dx, y: dragRef.current.baseY + dy });
  };

  const handleEnd = () => {
    dragRef.current = null;
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleEnd);
    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("touchend", handleEnd);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleEnd);
    };
  });

  const fitImage = () => {
    setZoom(1);
    setPos({ x: 0, y: 0 });
  };

  const previewMetrics = useMemo(() => {
    const naturalW = naturalSize.width || 0;
    const naturalH = naturalSize.height || 0;
    if (!naturalW || !naturalH) return null;
    const baseScale = Math.min(cropBox.width / naturalW, cropBox.height / naturalH);
    return {
      width: naturalW * baseScale,
      height: naturalH * baseScale,
    };
  }, [naturalSize.width, naturalSize.height, cropBox.width, cropBox.height]);

  const save = async () => {
    const img = imgRef.current;
    const stage = stageRef.current;
    if (!img || !stage || !loaded) return;

    const rect = stage.getBoundingClientRect();
    const naturalW = img.naturalWidth;
    const naturalH = img.naturalHeight;
    const boxW = cropBox.width;
    const boxH = cropBox.height;

    const baseScale = Math.min(boxW / naturalW, boxH / naturalH);
    const scale = baseScale * zoom;
    const renderedW = naturalW * scale;
    const renderedH = naturalH * scale;
    const imgLeft = rect.width / 2 - renderedW / 2 + pos.x;
    const imgTop = rect.height / 2 - renderedH / 2 + pos.y;
    const cropLeft = (rect.width - boxW) / 2;
    const cropTop = (rect.height - boxH) / 2;

    const sx = clamp((cropLeft - imgLeft) / scale, 0, naturalW);
    const sy = clamp((cropTop - imgTop) / scale, 0, naturalH);
    const sw = clamp(boxW / scale, 1, naturalW - sx);
    const sh = clamp(boxH / scale, 1, naturalH - sy);

    const outW = outputWidth || 900;
    const outH = Math.round(outW / (aspect || 1));
    const canvas = document.createElement("canvas");
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingQuality = "high";
    const cleanBg = String(backgroundColor || "#f7efe6").trim() || "#f7efe6";
    ctx.fillStyle = cleanBg;
    ctx.fillRect(0, 0, outW, outH);

    const outputFit = String(fitMode || "contain").toLowerCase() === "cover" ? "cover" : "contain";
    const fitScale = outputFit === "cover"
      ? Math.max(outW / naturalW, outH / naturalH)
      : Math.min(outW / naturalW, outH / naturalH);
    const drawScale = fitScale * zoom;
    const drawW = naturalW * drawScale;
    const drawH = naturalH * drawScale;
    const dx = outW / 2 - drawW / 2 + (pos.x / boxW) * outW;
    const dy = outH / 2 - drawH / 2 + (pos.y / boxH) * outH;
    ctx.drawImage(img, 0, 0, naturalW, naturalH, dx, dy, drawW, drawH);

    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      onComplete && onComplete({ blob, url });
    }, "image/jpeg", 0.92);
  };

  return (
    <div className="icp">
      <style>{styles}</style>
      <div className="icp-layout">
        <div
          ref={stageRef}
          className="icp-stage"
          onMouseDown={handleStart}
          onTouchStart={handleStart}
        >
          {imageUrl ? (
            <img
              ref={imgRef}
              src={imageUrl}
              alt="Ajustar"
              onLoad={(e) => {
                const target = e.currentTarget;
                setNaturalSize({ width: target.naturalWidth || 0, height: target.naturalHeight || 0 });
                setLoaded(true);
              }}
              style={{
                width: previewMetrics ? `${previewMetrics.width}px` : "auto",
                height: previewMetrics ? `${previewMetrics.height}px` : "auto",
                left: "50%",
                top: "50%",
                transform: `translate(-50%, -50%) translate(${pos.x}px, ${pos.y}px) scale(${zoom})`,
              }}
              draggable={false}
            />
          ) : null}
          <div className="icp-dim" />
          <div
            className="icp-crop"
            style={{ width: cropBox.width, height: cropBox.height }}
          >
            <span />
            <i />
          </div>
        </div>

        <aside className="icp-tools">
          <div>
            <span className="icp-kicker">Imagen</span>
            <h4>Logo completo</h4>
            <p>Ajusta el logo completo. Si el logo no llena todo el espacio, Loop agregará un fondo inteligente para que se vea premium.</p>
          </div>

          <label className="icp-slider">
            <span>Zoom</span>
            <input
              type="range"
              min="1"
              max="3"
              step="0.01"
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
            />
          </label>

          <button type="button" className="icp-soft" onClick={fitImage}>Centrar</button>
        </aside>
      </div>

      <div className="icp-actions">
        <button type="button" className="icp-secondary" onClick={onCancel}>Cancelar</button>
        <button type="button" className="icp-primary" onClick={save}>Guardar logo</button>
      </div>
    </div>
  );
}

const styles = `
  .icp { color: #0f172a; }
  .icp-layout { display: grid; grid-template-columns: minmax(0, 1fr) 250px; gap: 16px; align-items: stretch; }
  .icp-stage { position: relative; height: min(62vh, 560px); min-height: 420px; border-radius: 28px; overflow: hidden; background: radial-gradient(circle at top left, rgba(0,103,95,.12), transparent 34%), linear-gradient(135deg,#ffffff,#ecfdf5); cursor: grab; touch-action: none; display: grid; place-items: center; }
  .icp-stage:active { cursor: grabbing; }
  .icp-stage img { position: absolute; max-width: none; max-height: none; object-fit: contain; user-select: none; transition: transform 80ms linear; will-change: transform; }
  .icp-dim { position: absolute; inset: 0; background: rgba(255,255,255,.22); pointer-events: none; }
  .icp-crop { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); border: 2px solid rgba(255,255,255,0.92); border-radius: 18px; box-shadow: 0 0 0 999px rgba(15,23,42,.10); pointer-events: none; overflow: hidden; }
  .icp-crop span { position: absolute; inset: 33.333% 0 auto 0; border-top: 1px solid rgba(255,255,255,0.44); }
  .icp-crop span::after { content: ''; position: absolute; left: 0; right: 0; top: calc(100% + 33.333%); border-top: 1px solid rgba(255,255,255,0.44); }
  .icp-crop i { position: absolute; top: 0; bottom: 0; left: 33.333%; border-left: 1px solid rgba(255,255,255,0.44); }
  .icp-crop i::after { content: ''; position: absolute; top: 0; bottom: 0; left: calc(100% + 33.333%); border-left: 1px solid rgba(255,255,255,0.44); }
  .icp-tools { border-radius: 28px; border: 1px solid #e2e8f0; background: #f8fafc; padding: 16px; display: grid; align-content: start; gap: 16px; }
  .icp-kicker { color: ${BRAND}; font-size: 11px; font-weight: 950; letter-spacing: .12em; text-transform: uppercase; }
  .icp-tools h4 { margin: 7px 0 0; font-size: 25px; line-height: .95; letter-spacing: -.055em; font-weight: 950; }
  .icp-tools p { margin: 8px 0 0; color: #64748b; font-size: 13px; line-height: 1.42; font-weight: 750; }
  .icp-slider span { display: block; color: #334155; font-size: 13px; font-weight: 950; margin-bottom: 9px; }
  .icp-slider input { width: 100%; accent-color: ${BRAND}; }
  .icp-soft, .icp-secondary, .icp-primary { min-height: 46px; border-radius: 16px; font-weight: 950; cursor: pointer; }
  .icp-soft, .icp-secondary { border: 1px solid #e2e8f0; background: #fff; color: #334155; }
  .icp-primary { border: 0; background: linear-gradient(135deg, ${BRAND}, #269690); color: #fff; box-shadow: 0 14px 30px rgba(0,103,95,.20); }
  .icp-actions { margin-top: 14px; display: flex; justify-content: flex-end; gap: 10px; }
  .icp-actions button { padding: 0 18px; }
  @media (max-width: 780px) { .icp-layout { grid-template-columns: 1fr; } .icp-stage { height: 56vh; min-height: 330px; border-radius: 24px; } .icp-tools { border-radius: 24px; padding: 14px; } .icp-tools h4 { font-size: 22px; } .icp-actions { position: sticky; bottom: 0; display: grid; grid-template-columns: 1fr 1fr; background: #fff; padding-top: 10px; } .icp-actions button { width: 100%; } .icp-crop { max-width: 84vw !important; max-height: 42vh !important; } }


  /* LOOP IMAGE CROPPER — premium compact polish */
  .icp {
    color:#101828;
  }

  .icp-layout {
    grid-template-columns:minmax(0,1fr) 280px !important;
    gap:18px !important;
  }

  .icp-stage {
    height:min(58vh,500px) !important;
    min-height:360px !important;
    border-radius:26px !important;
    background:
      radial-gradient(circle at 15% 0%, rgba(0,103,95,.15), transparent 34%),
      linear-gradient(135deg,#ffffff 0%,#f0fdfa 100%) !important;
    box-shadow:inset 0 0 0 1px rgba(255,255,255,.72), 0 18px 42px rgba(15,23,42,.06) !important;
  }

  .icp-crop {
    border-radius:18px !important;
    border:2px solid rgba(255,255,255,.96) !important;
    box-shadow:0 0 0 999px rgba(15,23,42,.16), 0 18px 44px rgba(15,23,42,.12) !important;
  }

  .icp-tools {
    border-radius:24px !important;
    border:1px solid rgba(226,232,240,.95) !important;
    background:linear-gradient(180deg,#fff,#f8fffd) !important;
    box-shadow:0 14px 34px rgba(15,23,42,.045) !important;
    padding:18px !important;
  }

  .icp-tools h4 {
    font-size:22px !important;
  }

  .icp-tools p {
    font-size:12.5px !important;
    color:#667085 !important;
  }

  .icp-soft,
  .icp-secondary,
  .icp-primary {
    min-height:44px !important;
    border-radius:14px !important;
  }

  .icp-actions {
    margin-top:16px !important;
    padding-top:14px !important;
    border-top:1px solid rgba(226,232,240,.86) !important;
  }

  @media(max-width:780px){
    .icp-layout {
      grid-template-columns:1fr !important;
      gap:12px !important;
    }

    .icp-stage {
      height:48vh !important;
      min-height:290px !important;
      border-radius:22px !important;
    }

    .icp-tools {
      border-radius:20px !important;
      padding:14px !important;
    }

    .icp-actions {
      position:sticky !important;
      bottom:0 !important;
      grid-template-columns:1fr 1fr !important;
      background:#fff !important;
      z-index:3 !important;
    }
  }



  /* LOOP IMAGE CROPPER — background color correctness + proportional preview */
  .icp-stage {
    background:
      radial-gradient(circle at 15% 0%, rgba(0,103,95,.10), transparent 34%),
      linear-gradient(135deg,#ffffff 0%,#f8fffd 100%) !important;
  }

  .icp-crop {
    aspect-ratio:16/9 !important;
  }



  /* LOOP IMAGE CROPPER — exact contain output, no artificial 88% inset */
  .icp-stage img {
    object-fit:contain!important;
  }

`;
